/**
 * Main Layout Component
 * 
 * Combines sidebar navigation with main content area.
 * Used for all authenticated routes.
 * 
 * @module components/layout/MainLayout
 */

import React, { useEffect } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import { decodeJWT } from '@/utils/jwt';
import './MainLayout.css';

export const MainLayout: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === '1' || searchParams.get('embed') === 'true';

  // Add data attribute to body for route-specific CSS
  useEffect(() => {
    const pathname = location.pathname.replace('/', '') || 'home';
    document.body.setAttribute('data-route', pathname);
    if (isEmbed) {
      document.body.setAttribute('data-embed', 'true');
    } else {
      document.body.removeAttribute('data-embed');
    }
    return () => {
      document.body.removeAttribute('data-route');
      document.body.removeAttribute('data-embed');
    };
  }, [location.pathname, isEmbed]);

  // Embed only: inject global scrollbar-hiding style if not already added by index.html
  useEffect(() => {
    if (!isEmbed) return;
    if (document.getElementById('embed-hide-scrollbar')) return; // index.html already added it
    const style = document.createElement('style');
    style.id = 'embed-hide-scrollbar';
    style.textContent = `*,*::before,*::after{scrollbar-width:none!important;-ms-overflow-style:none!important}*::-webkit-scrollbar,*::before::-webkit-scrollbar,*::after::-webkit-scrollbar{display:none!important;width:0!important;height:0!important}`;
    document.head.appendChild(style);
    return () => document.getElementById('embed-hide-scrollbar')?.remove();
  }, [isEmbed]);

  // Workaround: Fix LoreBot disclaimer modal on /lorebot route
  // Auto-dismiss if user already accepted, otherwise attach click handler
  useEffect(() => {
    if (location.pathname === '/lorebot') {
      const DISCLAIMER_KEY = 'lorebot_disclaimer_accepted';
      
      // Call LoreBot backend API to save disclaimer preference
      const saveDisclaimerToBackend = async () => {
        try {
          const token = CommunicationBridge.getAccessToken();
          if (!token) {
            console.warn('[MainLayout] No auth token available, cannot save to backend');
            return;
          }

          const claims = decodeJWT(token);
          if (!claims || !claims.ref_user_id) {
            console.warn('[MainLayout] Could not get user ID from token');
            return;
          }

          const refUserId = claims.ref_user_id;
          
          // Determine environment and preference ID
          const env = process.env.REACT_APP_ENV || 'development';
          const isProd = env === 'prod' || env === 'production';
          const preferenceId = isProd ? 1 : 5; // prod: 1, nonprod: 5
          const apiBaseUrl = isProd 
            ? 'https://svcs-lore-00.endpoints.protected-00-349215.cloud.goog'
            : 'https://svcs-lore-00-nonprod.endpoints.protected-00-349215.cloud.goog';

          const url = `${apiBaseUrl}/services/lorebot-api/api/v1/me/preferences/${preferenceId}?ref_user_id=${refUserId}&preference_value_bool=true`;

          console.log('[MainLayout] Saving disclaimer to backend:', { refUserId, preferenceId, isProd });

          const response = await fetch(url, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            console.log('[MainLayout] ✅ Disclaimer saved to backend successfully');
          } else {
            console.warn('[MainLayout] ⚠️ Backend API returned non-OK status:', response.status);
          }
        } catch (error) {
          // Don't throw - we want localStorage to work even if API fails
          console.error('[MainLayout] ❌ Failed to save disclaimer to backend:', error);
        }
      };

      const handleDisclaimer = () => {
        const modal = document.querySelector('ion-modal.disclaimer-modal');
        const button = modal?.querySelector('ion-button');
        
        if (!modal || modal.getAttribute('is-open') !== 'true') return;
        
        // Auto-dismiss if already accepted
        if (localStorage.getItem(DISCLAIMER_KEY) === 'true') {
          modal.setAttribute('is-open', 'false');
          modal.remove();
          return;
        }
        
        // Show modal and attach click handler
        modal.classList.add('show-disclaimer');
        
        if (button) {
          const dismissModal = async () => {
            // Save to localStorage first (immediate UX)
            localStorage.setItem(DISCLAIMER_KEY, 'true');
            modal.setAttribute('is-open', 'false');
            modal.remove();
            
            // Then save to backend (for cross-device sync)
            await saveDisclaimerToBackend();
          };
          
          button.addEventListener('click', dismissModal);
        }
      };

      const timer = setTimeout(handleDisclaimer, 1000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Fix LoreBot text input visibility on mobile when keyboard appears
  useEffect(() => {
    if (location.pathname === '/lorebot') {
      const lorebotWrapper = document.querySelector('.mfe-wrapper.mfe-lorebot') as HTMLElement;
      
      const handleInputFocus = (e: Event) => {
        const target = e.target as HTMLElement;
        
        // Check if it's the LoreBot textarea
        if (
          target.tagName === 'TEXTAREA' ||
          target.classList.toString().includes('textArea') ||
          target.getAttribute('contenteditable') === 'true'
        ) {
          // Add keyboard-active class when focused
          if (lorebotWrapper) {
            lorebotWrapper.classList.add('keyboard-active');
          }
          
          // Wait for keyboard to appear (iOS needs delay)
          setTimeout(() => {
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          }, 300);
        }
      };

      const handleInputBlur = (e: Event) => {
        const target = e.target as HTMLElement;
        
        // Check if it's the LoreBot textarea
        if (
          target.tagName === 'TEXTAREA' ||
          target.classList.toString().includes('textArea') ||
          target.getAttribute('contenteditable') === 'true'
        ) {
          // Remove keyboard-active class when blurred
          if (lorebotWrapper) {
            lorebotWrapper.classList.remove('keyboard-active');
          }
        }
      };

      // Attach to focus and blur events
      document.addEventListener('focusin', handleInputFocus);
      document.addEventListener('focusout', handleInputBlur);

      return () => {
        document.removeEventListener('focusin', handleInputFocus);
        document.removeEventListener('focusout', handleInputBlur);
      };
    }
  }, [location.pathname]);

  return (
    <div className={`main-layout${isEmbed ? ' main-layout--embed' : ''}`}>
      {!isEmbed && <Sidebar />}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

